import type { EditorHost } from "@blocksuite/block-std";
import type { RootService } from "@blocksuite/blocks";
import type { GoogleCalendarEvent } from "@pique/core/modules/properties/services/calendar-types";
import type { SerializedAttendee } from "@pique/core/modules/properties/services/schema";
import {
	appSettingAtom,
	getCurrentStore,
	LiveData,
	Service,
	transcriptionCollapsedAtom,
} from "@toeverything/infra";

import { openSettingModalAtom } from "../atoms";
import { isMobileView } from "../components/app-sidebar/index.jotai";
import {
	AUGMENTATION_MODEL_DEFAULT,
	useGeminiTranscriptionAtom,
} from "../components/pique/setting-modal/atoms";

import { latestTranscriptionChunkSlot } from "../components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots";
import { getCurrentEditorHost } from "../components/blocksuite/block-suite-editor/utils";
import { generateIncrementalSummaryPromptComponents } from "../components/blocksuite/block-suite-mode-switch/augment-prompts";
import { enhancedNotify as notify } from "../components/shared/notification-utils";
import { TranscriptionSource } from "../hooks/transcription-source-config";
import type { FetchService } from "../modules/cloud";
import { UserCopilotQuotaService, UserQuotaService } from "../modules/cloud";
import { BackendError } from "../modules/cloud/error";
import type { AttendeesService } from "../modules/properties";
import { WorkspacePropertiesAdapter } from "../modules/properties/services/adapter";
import type { TranscriptionService as PropertiesTranscriptionService } from "../modules/properties/services/transcription-service";
import { getAwsTranscribeCredentials } from "../utils/aws-credentials";
import { AudioHandler } from "./audio-handler";

const SESSION_WARNING_MINUTES = 10;
const CREDENTIAL_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 min before expiry
const CREDENTIAL_MIN_REFRESH_MS = 60 * 1000; // Minimum 60s between refreshes
const CREDENTIAL_RETRY_MS = 2 * 60 * 1000; // Retry failed fetches after 2 min

export enum TranscriptionStatus {
	INACTIVE = "inactive",
	RECORDING = "recording",
	PAUSED = "paused",
}

export interface FullSessionTranscriptionData {
	text: string;
	source: "lecture" | "conversation" | "meeting";
	timestamp: number;
}

export interface TranscriptChunkData {
	text: string | null | undefined;
	timestamp: number;
	formattedTimestamp?: string;
	speaker?: string;
	confidence?: number;
	isFinal?: boolean;
	vendor?: "WebSocket" | "Deepgram" | "AwsTranscribe";
	id?: string;
	audioSource?: "mic" | "speaker";
}

export interface AmplitudeData {
	mic: number;
	speaker: number;
	scaled: number;
}

export interface MeetingEndedPromptEvent {
	timestamp: number;
	targetDocId: string | null;
	endedApps: string[];
	remainingTrackedApps: string[];
	reason: string;
}

export interface MeetingEndedPromptInput {
	endedApps?: string[];
	remainingTrackedApps?: string[];
	reason?: string;
}

export type AugmentationTriggerSource =
	| "cta"
	| "deep_link"
	| "meeting_ended_notification"
	| "unknown";

export interface LastSavedChunk {
	text: string;
	timestamp: number;
}

interface QuotaLimits {
	monthlyLimitSec: number | null;
	sessionLimitSec: number | null;
}

export class TranscriptionService extends Service {
	public readonly latestTranscriptionChunk$ =
		new LiveData<TranscriptChunkData | null>(null);

	public readonly sessionSeconds$ = new LiveData<number>(0);

	public readonly transcriptionStatus$ = new LiveData<TranscriptionStatus>(
		TranscriptionStatus.INACTIVE,
	);

	public readonly transcriptionSource$ = new LiveData<TranscriptionSource>(
		TranscriptionSource.NONE,
	);

	public readonly amplitude$ = new LiveData<AmplitudeData>({
		mic: 0,
		speaker: 0,
		scaled: 0,
	});

	private lastAmplitudeUpdateMs = 0;
	private pendingAmplitude: AmplitudeData | null = null;
	private readonly amplitudeThrottleMs = 16; // ~60fps for smooth animation

	public readonly startMeetingTranscribing$ = new LiveData<{
		workspaceId: string;
		event: GoogleCalendarEvent;
	} | null>(null);

	public readonly upcomingCalendarEvent$ =
		new LiveData<GoogleCalendarEvent | null>(null);

	public readonly triggerAugmentation$ = new LiveData<{
		timestamp: number;
		source: AugmentationTriggerSource;
	} | null>(null);

	public readonly meetingEndedPrompt$ =
		new LiveData<MeetingEndedPromptEvent | null>(null);

	public readonly createDocWithRecording$ = new LiveData<{
		id: string;
		timestamp: number;
		shrinkWindow?: boolean;
	} | null>(null);

	private selectedDeviceId: string | undefined = undefined;

	// Centralized last saved chunk tracking for deduplication
	private lastSavedChunk: LastSavedChunk | null = null;

	// Session-based chunk counter for unique IDs
	private sessionChunkCounter = 0;

	// Session-based timestamp offset to continue from last saved chunk timestamp
	private sessionTimestampOffsetSec = 0;

	// Snapshot of attendees at session start (used for mic speaker mapping)
	private sessionAttendeeCount = 0;

	private lastPersistedTimestampSec: number | null = null;
	private baselinePageId: string | null = null;
	private sessionPageId: string | null = null;

	private activeDocId: string | null = null;
	private activeEventId: string | null = null;

	private audioHandler: AudioHandler | null = null;

	private credentialRefreshTimeout: NodeJS.Timeout | null = null;
	private credentialExpirationMs: number | null = null;
	private credentialWatchdogId: NodeJS.Timeout | null = null;
	private static readonly CREDENTIAL_WATCHDOG_MS = 60_000;

	// Usage tracking based on the max finalized transcription timestamp
	private lastReportedSessionSec: number | null = null;
	private usageTimerId: number | null = null;
	private monthlyUsageSnapshotSec: number | null = null;
	private monthlyUsageSnapshotPeriod: string | null = null;
	private monthlyUsageSnapshotSessionSec = 0;
	private quotaShutdownInFlight = false;

	// Session-limit notification tracking
	private sessionLimitNotifyId: string | number | null = null;
	private lastLoggedRemainingSec: number | null = null;
	private wasBelowWarningThreshold = false;

	private static readonly SUMMARY_INTERVAL_MS = 180000; // 3 minutes

	private sessionSecondsUnlisten: (() => void) | undefined;
	private settingsUnlisten: (() => void) | undefined;
	private geminiAtomUnlisten: (() => void) | undefined;

	constructor(private readonly fetchService: FetchService) {
		super();

		this.audioHandler = new AudioHandler(this);

		const store = getCurrentStore();
		const useGoogleSpeech = store.get(useGeminiTranscriptionAtom);

		console.log(
			`[TranscriptionService] init: isTauri=${environment.isTauri}, useGoogleSpeech=${useGoogleSpeech}`,
		);

		if (environment.isTauri) {
			if (useGoogleSpeech) {
				console.log(
					"[TranscriptionService] Desktop: using Google Speech gateway (experimental)",
				);
				this.setTranscriptionSource(TranscriptionSource.WEB_GEMINI_FLASH);
			} else {
				console.log(
					"[TranscriptionService] Desktop: using native AWS Transcribe",
				);
				this.initializeTranscriptionProvider().catch(() => {});
				this.subscribeToSettingsChanges();
			}
			this.credentialWatchdogId = setInterval(() => {
				this.ensureFreshCredentials().catch(() => {});
			}, TranscriptionService.CREDENTIAL_WATCHDOG_MS);
		} else {
			this.setTranscriptionSource(
				useGoogleSpeech
					? TranscriptionSource.WEB_GEMINI_FLASH
					: TranscriptionSource.WEB_DEEPGRAM,
			);
		}

		// React to live toggle changes on both platforms
		this.geminiAtomUnlisten = store.sub(useGeminiTranscriptionAtom, () => {
			const next = store.get(useGeminiTranscriptionAtom);
			if (next) {
				console.log(
					"[TranscriptionService] Google Speech toggle ON → source=web-gemini-flash",
				);
				this.setTranscriptionSource(TranscriptionSource.WEB_GEMINI_FLASH);
			} else if (environment.isTauri) {
				console.log(
					"[TranscriptionService] Google Speech toggle OFF → reinitializing desktop provider",
				);
				this.initializeTranscriptionProvider().catch(() => {});
			} else {
				console.log(
					"[TranscriptionService] Google Speech toggle OFF → source=web-deepgram",
				);
				this.setTranscriptionSource(TranscriptionSource.WEB_DEEPGRAM);
			}
		});

		// Subscribe to finalized session length changes to enforce quota
		const ssSub = this.sessionSeconds$.subscribe(() => {
			void this.enforceSessionTimeQuota(true).catch(() => {});
		});
		this.sessionSecondsUnlisten = () => ssSub.unsubscribe();
	}

	private async initializeTranscriptionProvider(): Promise<void> {
		await this.updateTranscriptionProvider();
	}

	private subscribeToSettingsChanges(): void {
		const store = getCurrentStore();

		// Subscribe to atom changes
		this.settingsUnlisten?.();
		this.settingsUnlisten = store.sub(appSettingAtom, () => {
			// Only update if the relevant setting changed
			this.updateTranscriptionProvider().catch(() => {
				// Silent error handling
			});
		});
	}

	private async updateTranscriptionProvider(): Promise<void> {
		try {
			// If Google Speech gateway is active, don't override with native provider
			const store = getCurrentStore();
			if (store.get(useGeminiTranscriptionAtom)) {
				return;
			}

			const flagsModule = await import("../tauri/plugins/flags");
			const flagsCommands = flagsModule.commands;

			this.fetchKeysInBackground();

			const appSettings = store.get(appSettingAtom);
			const enableOnlineTranscription = appSettings.enableOnlineTranscription;

			if (enableOnlineTranscription) {
				await flagsCommands.enable("CloudPreview");
				this.setTranscriptionSource(TranscriptionSource.DESKTOP_AWS_TRANSCRIBE);
			} else {
				await flagsCommands.disable("CloudPreview");
				this.setTranscriptionSource(TranscriptionSource.LOCAL_STT);
			}
		} catch {
			// Silent error handling
		}
	}

	private fetchKeysInBackground(): void {
		if (environment.isTauri) {
			this.fetchAndScheduleAwsCredentials().catch(() => {});
		}
	}

	public async fetchAndScheduleAwsCredentials(): Promise<boolean> {
		try {
			const creds = await getAwsTranscribeCredentials(this.fetchService);
			const nowMs = Date.now();
			const expiryMs =
				creds.expiration instanceof Date
					? creds.expiration.getTime()
					: new Date(creds.expiration as unknown as string).getTime();
			if (!Number.isFinite(expiryMs) || expiryMs <= nowMs) {
				throw new Error(
					`Invalid AWS credential expiration: ${String(creds.expiration)}`,
				);
			}
			this.credentialExpirationMs = expiryMs;
			if (environment.isTauri) {
				const { commands: listenerCommands } = await import(
					"../tauri/plugins/listener"
				);
				await listenerCommands.setAwsCredentialExpiration(expiryMs);
			}
			const delayMs = Math.max(
				CREDENTIAL_MIN_REFRESH_MS,
				expiryMs - nowMs - CREDENTIAL_BUFFER_MS,
			);
			this.scheduleCredentialRefresh(delayMs);
			return true;
		} catch (err) {
			// Don't retry when unauthenticated — credentials will be fetched
			// on-demand when recording actually starts after the user signs in.
			if (err instanceof BackendError && err.status === 401) {
				return false;
			}
			if (
				err instanceof Error &&
				err.constructor.name === "UnauthorizedError"
			) {
				return false;
			}
			this.scheduleCredentialRefresh(CREDENTIAL_RETRY_MS);
			return false;
		}
	}

	public areCredentialsFresh(): boolean {
		if (!this.credentialExpirationMs) return false;
		return this.credentialExpirationMs - Date.now() > CREDENTIAL_BUFFER_MS;
	}

	/** Ensures vault credentials are fresh before use. Always fetches if stale. */
	public async ensureFreshCredentials(): Promise<boolean> {
		if (this.areCredentialsFresh()) return true;
		return this.fetchAndScheduleAwsCredentials();
	}

	private scheduleCredentialRefresh(delayMs: number): void {
		this.clearCredentialRefresh();
		this.credentialRefreshTimeout = setTimeout(() => {
			this.fetchAndScheduleAwsCredentials().catch(() => {});
		}, delayMs);
	}

	private clearCredentialRefresh(): void {
		if (this.credentialRefreshTimeout) {
			clearTimeout(this.credentialRefreshTimeout);
			this.credentialRefreshTimeout = null;
		}
	}

	public startMeetingTranscribing(
		workspaceId: string,
		event: GoogleCalendarEvent,
	) {
		this.startMeetingTranscribing$.next({ workspaceId, event });
	}

	public clearMeetingTranscribing() {
		this.startMeetingTranscribing$.next(null);
	}

	public setUpcomingCalendarEvent(event: GoogleCalendarEvent | null) {
		this.upcomingCalendarEvent$.next(event);
	}

	public triggerAugmentation(source: AugmentationTriggerSource = "unknown") {
		this.triggerAugmentation$.next({ timestamp: Date.now(), source });
	}

	public clearTriggerAugmentation() {
		this.triggerAugmentation$.next(null);
	}

	public queueMeetingEndedPrompt(input?: MeetingEndedPromptInput) {
		this.meetingEndedPrompt$.next({
			timestamp: Date.now(),
			targetDocId: this.activeDocId,
			endedApps: input?.endedApps ?? [],
			remainingTrackedApps: input?.remainingTrackedApps ?? [],
			reason: input?.reason ?? "unknown",
		});
	}

	public clearMeetingEndedPrompt() {
		this.meetingEndedPrompt$.next(null);
	}

	public triggerCreateDocWithRecording(options?: { shrinkWindow?: boolean }) {
		const id =
			globalThis.crypto?.randomUUID?.() ||
			`cdr-${Date.now()}-${Math.random().toString(36).slice(2)}`;
		this.createDocWithRecording$.next({
			id,
			timestamp: Date.now(),
			shrinkWindow: options?.shrinkWindow ?? false,
		});
	}

	setLatestTranscriptionChunk(chunk: TranscriptChunkData) {
		if (
			!chunk ||
			!chunk.text ||
			typeof chunk.text !== "string" ||
			chunk.text.trim() === ""
		) {
			return;
		}

		// Only emit chunks when transcription is active
		const currentStatus = this.transcriptionStatus$.value;
		if (currentStatus === TranscriptionStatus.INACTIVE) {
			return;
		}

		// Apply session timestamp offset so new sessions continue from last saved timestamp
		const adjustedTimestamp =
			(typeof chunk.timestamp === "number" ? chunk.timestamp : 0) +
			this.sessionTimestampOffsetSec;

		// Add unique ID if not already present
		const chunkWithId = {
			...chunk,
			timestamp: adjustedTimestamp,
			id: chunk.id || `session-${++this.sessionChunkCounter}`,
		};

		this.latestTranscriptionChunk$.next(chunkWithId);
		latestTranscriptionChunkSlot.emit(chunkWithId);
	}

	setAmplitude(amplitude: AmplitudeData) {
		const now = Date.now();
		const elapsed = now - this.lastAmplitudeUpdateMs;

		if (elapsed >= this.amplitudeThrottleMs) {
			const current = this.amplitude$.value;
			if (
				current.mic !== amplitude.mic ||
				current.speaker !== amplitude.speaker ||
				current.scaled !== amplitude.scaled
			) {
				this.amplitude$.next(amplitude);
				this.lastAmplitudeUpdateMs = now;
			}
			this.pendingAmplitude = null;
		} else {
			this.pendingAmplitude = amplitude;
			if (!this.amplitudeFlushScheduled) {
				this.amplitudeFlushScheduled = true;
				requestAnimationFrame(() => {
					this.amplitudeFlushScheduled = false;
					if (this.pendingAmplitude) {
						const pending = this.pendingAmplitude;
						this.pendingAmplitude = null;
						this.setAmplitude(pending);
					}
				});
			}
		}
	}

	private amplitudeFlushScheduled = false;

	pauseTranscription(): void {
		this.setTranscriptionStatus(TranscriptionStatus.PAUSED);
	}

	resumeTranscription(): void {
		this.setTranscriptionStatus(TranscriptionStatus.RECORDING);
	}

	stopTranscription(): void {
		this.setTranscriptionStatus(TranscriptionStatus.INACTIVE);
	}

	setTranscriptionStatus(nextStatus: TranscriptionStatus): void {
		this.executeStatusChange(nextStatus).catch(() => {});
	}

	private async executeStatusChange(
		nextStatus: TranscriptionStatus,
	): Promise<void> {
		switch (nextStatus) {
			case TranscriptionStatus.RECORDING: {
				// Block if another device is actively recording (non-stale, < 6h)
				if (this.activeEventId) {
					const active = this.getActiveRecordingFromAdapter();
					const isFresh =
						active && Date.now() - active.startedAt < 6 * 60 * 60 * 1000;
					if (active && isFresh && active.deviceType !== "desktop") {
						await notify.warning(
							{
								title: "Recording in progress",
								message: "This meeting is being recorded on another device.",
								rootAttrs: {
									style: {
										backgroundColor: "var(--affine-background-primary-color)",
										color: "var(--affine-text-primary-color)",
										borderColor: "rgb(140, 92, 245)",
										boxShadow: "var(--affine-menu-shadow)",
										borderRadius: "20px",
										border: "1px solid rgb(140, 92, 245)",
									},
								},
							},
							{
								duration: 5000,
								position: "bottom-center",
								style: { borderRadius: "10px" },
							},
						);
						return;
					}
				}

				const canStart = await this.checkMonthlyQuotaBeforeStart();
				if (!canStart) {
					return;
				}
				this.quotaShutdownInFlight = false;

				const isMobileViewValue = getCurrentStore().get(isMobileView);
				if (
					!environment.isTauri &&
					!environment.isPiqueMobile &&
					!isMobileViewValue
				) {
					await notify.warning(
						{
							title: "Web Recording",
							message: "Please use the Desktop App to record virtual meetings",
							action: {
								label: "Get Desktop",
								onClick: () => {
									window.open("https://pique.so", "_blank");
								},
								autoClose: true,
							},
							rootAttrs: {
								style: {
									backgroundColor: "var(--affine-background-primary-color)",
									color: "var(--affine-text-primary-color)",
									borderColor: "rgb(140, 92, 245)",
									boxShadow: "var(--affine-menu-shadow)",
									borderRadius: "20px",
									border: "1px solid rgb(140, 92, 245)",
								},
							},
						},
						{
							duration: 8000,
							position: "bottom-center",
							style: { borderRadius: "10px" },
						},
					);
				}

				// Only reset session state when starting fresh (from INACTIVE), not when resuming
				const isResumingFromPause =
					this.transcriptionStatus$.value === TranscriptionStatus.PAUSED;

				// Snapshot attendee count at session start for mic speaker mapping
				if (!isResumingFromPause) {
					this.sessionAttendeeCount = 0;
					try {
						const host = getCurrentEditorHost();
						const pageId = host?.doc?.id;
						if (pageId) {
							const pageService =
								host.spec.getService<RootService>("affine:page");
							const attendeesSvc = pageService?.attendeesService as unknown as
								| AttendeesService
								| undefined;
							const attendees = attendeesSvc?.getPageAttendees
								? attendeesSvc.getPageAttendees(pageId)
								: [];
							if (Array.isArray(attendees)) {
								this.sessionAttendeeCount = attendees.length;
							}
						}
					} catch {}

					// Only reset chunk counter when starting fresh, not resuming
					this.sessionChunkCounter = 0;

					this.sessionPageId =
						this.normalizePageId(this.activeDocId) ??
						this.normalizePageId(getCurrentEditorHost()?.doc?.id);
				}

				if (
					this.sessionPageId === null ||
					this.baselinePageId !== this.sessionPageId
				) {
					this.lastSavedChunk = null;
					this.lastPersistedTimestampSec = null;
					this.baselinePageId = this.sessionPageId;
				}
				const last =
					this.lastPersistedTimestampSec ??
					this.lastSavedChunk?.timestamp ??
					0;
				this.sessionTimestampOffsetSec = last;
				this.syncSessionSeconds(last);

				this.transcriptionStatus$.next(TranscriptionStatus.RECORDING);

				// Mark active recording in shared Yjs doc for cross-device detection
				if (this.activeDocId && this.activeEventId) {
					this.writeActiveRecordingToAdapter(
						this.activeDocId,
						this.activeEventId,
					);
				}

				// Centralized uncollapse with delay to allow cleanup
				try {
					setTimeout(() => {
						const store = getCurrentStore();
						store.set(transcriptionCollapsedAtom, false);
					}, 500);
				} catch {
					// ignore collapse failures in non-UI contexts
				}
				// Initialize last reported session seconds from current session time
				this.lastReportedSessionSec = this.getCurrentSessionSeconds();
				if (this.usageTimerId) {
					window.clearInterval(this.usageTimerId);
					this.usageTimerId = null;
				}
				this.usageTimerId = window.setInterval(() => {
					void this.flushUsageReporting().catch(() => {});
					// Piggyback incremental summary attempt on the same tick
					void this.tryCreateIncrementalSummary().catch(() => {});
					// Also check session time quota each tick
					void this.enforceSessionTimeQuota().catch(() => {});
				}, TranscriptionService.SUMMARY_INTERVAL_MS);

				try {
					const uq = this.framework.get(UserQuotaService);
					uq.quota.revalidate();
				} catch {}
				break;
			}
			case TranscriptionStatus.PAUSED: {
				try {
					if (this.usageTimerId) {
						window.clearInterval(this.usageTimerId);
						this.usageTimerId = null;
					}
					void this.flushUsageReporting().catch(() => {});
					if (this.sessionLimitNotifyId !== null) {
						notify.dismiss(this.sessionLimitNotifyId);
						this.sessionLimitNotifyId = null;
					}
				} finally {
					this.lastReportedSessionSec = null;
				}
				this.transcriptionStatus$.next(TranscriptionStatus.PAUSED);

				break;
			}
			case TranscriptionStatus.INACTIVE: {
				try {
					if (this.usageTimerId) {
						window.clearInterval(this.usageTimerId);
						this.usageTimerId = null;
					}
					void this.flushUsageReporting().catch(() => {});
					if (this.sessionLimitNotifyId !== null) {
						notify.dismiss(this.sessionLimitNotifyId);
						this.sessionLimitNotifyId = null;
					}
				} finally {
					this.lastReportedSessionSec = null;
				}
				// Clear active recording from shared Yjs doc
				if (this.activeDocId) {
					this.clearActiveRecordingFromAdapter(this.activeDocId);
				}
				this.activeDocId = null;
				this.activeEventId = null;

				this.clearTranscription();
				this.clearMeetingTranscribing();
				try {
					const store = getCurrentStore();
					store.set(transcriptionCollapsedAtom, true);
				} catch {}
				break;
			}
		}
	}

	private async flushUsageReporting(): Promise<void> {
		const currentSessionSec = this.getCurrentSessionSeconds();
		if (this.lastReportedSessionSec === null) {
			this.lastReportedSessionSec = currentSessionSec;
			return;
		}
		const deltaSec = Math.max(
			0,
			currentSessionSec - this.lastReportedSessionSec,
		);
		if (deltaSec <= 0) return;
		this.lastReportedSessionSec = currentSessionSec;
		await this.framework
			.get(UserCopilotQuotaService)
			.reportMonthlyTranscriptionUsage(deltaSec, this.getCurrentUsagePeriod());
	}

	setTranscriptionSource(source: TranscriptionSource) {
		if (this.transcriptionSource$.value !== source) {
			console.log(
				`[TranscriptionService] Source changed: ${this.transcriptionSource$.value} → ${source}`,
			);
			this.transcriptionSource$.next(source);
		}
	}

	clearTranscription() {
		this.transcriptionStatus$.next(TranscriptionStatus.INACTIVE);
		this.latestTranscriptionChunk$.next(null);
		this.sessionSeconds$.next(0);
		this.amplitude$.next({ mic: 0, speaker: 0, scaled: 0 });
		this.lastSavedChunk = null;
		this.sessionChunkCounter = 0;
		this.sessionTimestampOffsetSec = 0;
		this.sessionPageId = null;
		this.monthlyUsageSnapshotSessionSec = 0;
		this.quotaShutdownInFlight = false;
	}

	// Global deduplication methods
	shouldSkipDuplicateChunk(text: string): boolean {
		return this.lastSavedChunk !== null && this.lastSavedChunk.text === text;
	}

	private normalizePageId(docId: string | null | undefined): string | null {
		if (!docId) return null;
		return docId.endsWith("-augmented")
			? docId.slice(0, -"-augmented".length)
			: docId;
	}

	private resolveBaselineWriter(pageId: string): string | null {
		const normalized = this.normalizePageId(pageId);
		if (normalized === null) return null;
		if (
			this.transcriptionStatus$.value !== TranscriptionStatus.INACTIVE &&
			this.sessionPageId !== null &&
			normalized !== this.sessionPageId
		) {
			return null;
		}
		return normalized;
	}

	private latchSessionOffsetBeforeFirstChunk(seconds: number): void {
		if (
			this.transcriptionStatus$.value === TranscriptionStatus.RECORDING &&
			this.sessionChunkCounter === 0
		) {
			this.sessionTimestampOffsetSec = seconds;
		}
	}

	updateLastSavedChunk(text: string, timestamp: number, pageId: string): void {
		const writerPageId = this.resolveBaselineWriter(pageId);
		if (writerPageId === null) return;

		const normalizedTimestamp = Math.max(0, Math.floor(timestamp));
		const currentMax = this.getCurrentSessionSeconds();
		const nextMax = Math.max(currentMax, normalizedTimestamp);

		this.lastSavedChunk = { text, timestamp: nextMax };
		this.lastPersistedTimestampSec = nextMax;
		this.baselinePageId = writerPageId;
		this.latchSessionOffsetBeforeFirstChunk(nextMax);
		this.syncSessionSeconds(nextMax);
	}

	setTimestampBaseline(seconds: number, pageId: string): void {
		const writerPageId = this.resolveBaselineWriter(pageId);
		if (writerPageId === null) return;

		const normalizedSeconds = Math.max(0, Math.floor(seconds));
		this.lastPersistedTimestampSec = normalizedSeconds;
		this.baselinePageId = writerPageId;
		this.latchSessionOffsetBeforeFirstChunk(normalizedSeconds);
		this.syncSessionSeconds(normalizedSeconds);
	}

	// Simple device selection (browser-only)
	setSelectedDeviceId(deviceId: string | undefined): void {
		this.selectedDeviceId = deviceId;
	}

	getSelectedDeviceId(): string | undefined {
		return this.selectedDeviceId;
	}

	setActiveDocContext(docId: string, eventId?: string): void {
		this.activeDocId = this.normalizePageId(docId);
		this.activeEventId = eventId ?? null;
	}

	private getPropertiesAdapter(): WorkspacePropertiesAdapter | null {
		try {
			return this.framework.get(WorkspacePropertiesAdapter);
		} catch {
			return null;
		}
	}

	private getActiveRecordingFromAdapter() {
		return this.getPropertiesAdapter()?.activeRecording$.value ?? null;
	}

	private writeActiveRecordingToAdapter(docId: string, eventId: string): void {
		this.getPropertiesAdapter()?.setActiveRecording(docId, eventId);
	}

	private clearActiveRecordingFromAdapter(docId: string): void {
		this.getPropertiesAdapter()?.clearActiveRecording(docId);
	}

	getSessionAttendeeCount(): number {
		return this.sessionAttendeeCount;
	}

	dispose(): void {
		this.clearCredentialRefresh();
		if (this.credentialWatchdogId) {
			clearInterval(this.credentialWatchdogId);
			this.credentialWatchdogId = null;
		}
		if (this.usageTimerId) {
			window.clearInterval(this.usageTimerId);
			this.usageTimerId = null;
		}
		if (this.audioHandler) {
			this.audioHandler.dispose();
			this.audioHandler = null;
		}
		if (this.sessionSecondsUnlisten) {
			this.sessionSecondsUnlisten();
			this.sessionSecondsUnlisten = undefined;
		}
		if (this.settingsUnlisten) {
			this.settingsUnlisten();
			this.settingsUnlisten = undefined;
		}
		if (this.geminiAtomUnlisten) {
			this.geminiAtomUnlisten();
			this.geminiAtomUnlisten = undefined;
		}
		super.dispose();
	}

	// Centralized incremental summary creation
	private async tryCreateIncrementalSummary(): Promise<void> {
		const status = this.transcriptionStatus$.value;
		// Gate: only when actively recording or paused
		if (
			status !== TranscriptionStatus.RECORDING &&
			status !== TranscriptionStatus.PAUSED
		) {
			return;
		}

		try {
			const host: EditorHost | null = getCurrentEditorHost();
			if (!host?.doc) {
				return;
			}
			const pageService = host.spec.getService<RootService>("affine:page");
			const propertiesSvc = pageService?.transcriptionService as unknown as
				| PropertiesTranscriptionService
				| undefined;
			if (!propertiesSvc) {
				return;
			}

			const pageId = host.doc.id;
			const lastProcessed = propertiesSvc.getLastProcessedTsMs(pageId);
			const newChunks = propertiesSvc.getNewChunksSinceMs(
				pageId,
				lastProcessed,
			);
			if (newChunks.length === 0) return;

			const upToForPrev =
				lastProcessed > 0 ? lastProcessed : Number.MAX_SAFE_INTEGER;
			const previousSummary = propertiesSvc.fetchSummaryUpToTsMs(
				pageId,
				upToForPrev,
			);
			const lastChunk = newChunks[newChunks.length - 1];
			if (!lastChunk) return;
			const toTsMs = lastChunk.tsMs;
			if (propertiesSvc.getLastProcessedTsMs(pageId) >= toTsMs) {
				return;
			}

			const ers =
				typeof pageService?.getSidebarEdgelessRootService === "function"
					? await pageService.getSidebarEdgelessRootService()
					: undefined;
			if (!ers) {
				return;
			}

			let pageAttendees: SerializedAttendee[] | undefined;
			try {
				const attendeesSvc = pageService.attendeesService as unknown as
					| AttendeesService
					| undefined;
				if (attendeesSvc && pageId) {
					pageAttendees = attendeesSvc.getPageAttendees(pageId);
				}
			} catch {}

			const { IncrementalSummaryResponseSchema } = await import(
				"../components/blocksuite/block-suite-mode-switch/augment-schema"
			);

			const { instructions, content } =
				generateIncrementalSummaryPromptComponents(
					previousSummary,
					newChunks,
					pageAttendees,
				);
			const prompt = `${instructions}\n\n${content}`;

			const summaryRaw = await ers
				.askCopilotOneOff(prompt, {
					model: AUGMENTATION_MODEL_DEFAULT,
					temperature: 0.1,
				})
				.catch(() => null);

			if (summaryRaw) {
				try {
					const json = IncrementalSummaryResponseSchema.parse(summaryRaw);
					if (
						json.hasNew &&
						Array.isArray(json.items) &&
						json.items.length > 0
					) {
						propertiesSvc.appendIncrementalSummary(pageId, toTsMs, json.items);
					}
				} catch {}
			}
		} catch {
			// Silent error handling
		}
	}

	private async checkMonthlyQuotaBeforeStart(): Promise<boolean> {
		try {
			const { monthlyLimitSec } = this.getQuotaLimits();
			if (monthlyLimitSec === null) {
				return true;
			}

			const monthlyUsageSnapshotSec =
				await this.refreshMonthlyUsageSnapshot(true);
			const projectedUsageSec =
				monthlyUsageSnapshotSec + this.getCurrentSessionSeconds();

			if (projectedUsageSec >= monthlyLimitSec) {
				await notify.error(
					{
						title: "Plan limit reached",
						message:
							"You have exceeded the transcription limits for your current plan.",
						action: {
							label: "Upgrade Plan",
							onClick: () => {
								this.openPlansPage();
							},
							autoClose: true,
						},
						rootAttrs: {
							style: {
								backgroundColor: "var(--affine-background-primary-color)",
								color: "var(--affine-text-primary-color)",
								borderColor: "var(--affine-background-error-color)",
								boxShadow: "var(--affine-menu-shadow)",
								borderRadius: "20px",
								border: "1px solid var(--affine-background-error-color)",
							},
						},
					},
					{
						duration: 8000,
						position: "bottom-center",
						style: { borderRadius: "10px" },
					},
				);
				return false;
			}

			return true;
		} catch {
			return true;
		}
	}

	private async enforceSessionTimeQuota(forceLog = false): Promise<void> {
		if (this.transcriptionStatus$.value !== TranscriptionStatus.RECORDING)
			return;

		const elapsedSec = this.sessionSeconds$.value ?? 0;
		const { monthlyLimitSec, sessionLimitSec } = this.getQuotaLimits();
		const remainingSec =
			sessionLimitSec === null
				? Number.POSITIVE_INFINITY
				: Math.max(0, sessionLimitSec - elapsedSec);

		if (forceLog || this.lastLoggedRemainingSec !== remainingSec) {
			this.lastLoggedRemainingSec = remainingSec;
		}

		// Warning threshold
		const warnBeforeSec = SESSION_WARNING_MINUTES * 60;

		// Log crossings of the warning threshold for debugging
		const underThreshold = remainingSec > 0 && remainingSec <= warnBeforeSec;
		if (underThreshold && !this.wasBelowWarningThreshold) {
			// eslint-disable-next-line no-console
			this.wasBelowWarningThreshold = true;
		} else if (!underThreshold && this.wasBelowWarningThreshold) {
			// eslint-disable-next-line no-console
			this.wasBelowWarningThreshold = false;
		}

		// Show or update a persistent countdown when under threshold
		if (underThreshold) {
			// Only show if not already on screen
			if (!this.sessionLimitNotifyId) {
				this.sessionLimitNotifyId = await notify.warning(
					{
						title: "Approaching session limit",
						message: "Upgrade your plan for longer recordings.",
						url: undefined,
						timeout: 10000,
						action: {
							label: "Upgrade Plan",
							onClick: () => {
								this.openPlansPage();
							},
							autoClose: true,
						},
						rootAttrs: {
							style: {
								backgroundColor: "var(--affine-background-primary-color)",
								color: "var(--affine-text-primary-color)",
								borderColor: "rgb(140, 92, 245)",
								boxShadow: "var(--affine-menu-shadow)",
								borderRadius: "20px",
								border: "1px solid rgb(140, 92, 245)",
							},
						},
					},
					{
						duration: Infinity,
						position: "bottom-center",
						style: { borderRadius: "10px" },
					},
				);
			}
		} else if (remainingSec > warnBeforeSec && this.sessionLimitNotifyId) {
			notify.dismiss(this.sessionLimitNotifyId);
			this.sessionLimitNotifyId = null;
		}

		// Enforce cap
		if (remainingSec === 0) {
			await this.handleQuotaExceeded("session");
			return;
		}

		if (monthlyLimitSec === null) {
			return;
		}

		const monthlyUsageSnapshotSec = await this.refreshMonthlyUsageSnapshot();
		const monthlyRemainingSec = Math.max(
			0,
			monthlyLimitSec -
				this.getProjectedMonthlyUsageSec(monthlyUsageSnapshotSec),
		);

		if (monthlyRemainingSec === 0) {
			await this.handleQuotaExceeded("monthly");
		}
	}

	private openPlansPage(): void {
		const store = getCurrentStore();
		store.set(openSettingModalAtom, {
			activeTab: "plans",
			open: true,
		});
	}

	private getCurrentSessionSeconds(): number {
		return Math.max(0, Math.floor(this.sessionSeconds$.value ?? 0));
	}

	private syncSessionSeconds(seconds: number): void {
		const normalizedSeconds = Math.max(0, Math.floor(seconds));
		if (normalizedSeconds === this.sessionSeconds$.value) {
			return;
		}
		this.sessionSeconds$.next(normalizedSeconds);
	}

	private getQuotaLimits(): QuotaLimits {
		const quotaSvc = this.framework.get(UserQuotaService);
		const quota = quotaSvc.quota.quota$.value;

		return {
			monthlyLimitSec: this.normalizeQuotaMinutesToSeconds(
				quota?.transcriptionMonthlyMinutesLimit,
			),
			sessionLimitSec: this.normalizeQuotaMinutesToSeconds(
				quota?.transcriptionSessionMinutesLimit,
			),
		};
	}

	private normalizeQuotaMinutesToSeconds(value: unknown): number | null {
		const parsed =
			typeof value === "number"
				? value
				: typeof value === "string"
					? Number(value)
					: NaN;

		if (!Number.isFinite(parsed) || parsed < 0) {
			return null;
		}

		return Math.max(0, Math.floor(parsed * 60));
	}

	private getCurrentUsagePeriod(): string {
		return new Date().toISOString().slice(0, 7).replace("-", "");
	}

	private async refreshMonthlyUsageSnapshot(force = false): Promise<number> {
		const period = this.getCurrentUsagePeriod();
		if (
			!force &&
			this.monthlyUsageSnapshotPeriod === period &&
			this.monthlyUsageSnapshotSec !== null
		) {
			return this.monthlyUsageSnapshotSec;
		}

		const usage = await this.framework
			.get(UserCopilotQuotaService)
			.fetchMonthlyTranscriptionUsage(period);
		const usedSeconds = Math.max(0, Math.floor(usage?.usedSeconds ?? 0));
		this.monthlyUsageSnapshotPeriod = period;
		this.monthlyUsageSnapshotSec = usedSeconds;
		this.monthlyUsageSnapshotSessionSec = this.getCurrentSessionSeconds();
		return usedSeconds;
	}

	private getProjectedMonthlyUsageSec(monthlyUsageSnapshotSec: number): number {
		const growthSinceSnapshot = Math.max(
			0,
			this.getCurrentSessionSeconds() - this.monthlyUsageSnapshotSessionSec,
		);
		return monthlyUsageSnapshotSec + growthSinceSnapshot;
	}

	private async handleQuotaExceeded(
		quotaType: "monthly" | "session",
	): Promise<void> {
		if (this.quotaShutdownInFlight) {
			return;
		}
		this.quotaShutdownInFlight = true;

		if (this.sessionLimitNotifyId) {
			notify.dismiss(this.sessionLimitNotifyId);
			this.sessionLimitNotifyId = null;
		}

		this.stopTranscription();

		if (quotaType === "monthly") {
			await notify.warning(
				{
					title: "Plan limit reached",
					message:
						"Monthly transcription limit reached. Upgrade for more time.",
					action: {
						label: "Upgrade Plan",
						onClick: () => {
							this.openPlansPage();
						},
						autoClose: true,
					},
					rootAttrs: {
						style: {
							backgroundColor: "var(--affine-background-primary-color)",
							color: "var(--affine-text-primary-color)",
							borderColor: "rgb(140, 92, 245)",
							boxShadow: "var(--affine-menu-shadow)",
							borderRadius: "20px",
							border: "1px solid rgb(140, 92, 245)",
						},
					},
				},
				{
					duration: 8000,
					position: "bottom-center",
					style: { borderRadius: "10px" },
				},
			);
			return;
		}

		await notify.warning(
			{
				title: "Session limit reached",
				message:
					"Session limit reached. Upgrade your plan for longer recordings.",
				action: {
					label: "Upgrade Plan",
					onClick: () => {
						this.openPlansPage();
					},
					autoClose: true,
				},
				rootAttrs: {
					style: {
						backgroundColor: "var(--affine-background-primary-color)",
						color: "var(--affine-text-primary-color)",
						borderColor: "rgb(140, 92, 245)",
						boxShadow: "var(--affine-menu-shadow)",
						borderRadius: "20px",
						border: "1px solid rgb(140, 92, 245)",
					},
				},
			},
			{
				duration: 8000,
				position: "bottom-center",
				style: { borderRadius: "10px" },
			},
		);
	}
}
